import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ButtonComponent } from 'src/app/_components/buttons/button/button.component';
import { IconButtonComponent } from 'src/app/_components/buttons/icon-button/icon-button.component';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import {
  LicenseType,
  LICENSE_LIMITS,
  LicenseDisplay,
} from 'src/app/_models/license-type';
import { OrganisationIndexView } from 'src/app/_models/organisation';
import {
  OGI_SUPER_ADMIN_ONLY,
  Role,
  SUPER_ADMIN_ROLE_LIST,
} from 'src/app/_models/role';
import { TabOptions } from 'src/app/_models/tab-options';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ModalService } from 'src/app/_services/modal.service';
import { OrganisationService } from 'src/app/_services/organisation.service';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { ModalComponent } from 'src/app/_components/modals/modal.component';
import { ValidationModalComponent } from 'src/app/_components/modals/validation-modal/validation-modal.component';
import { RoleCheckPipe } from 'src/app/_helpers/role-check.pipe';
import { resolveLabOwnership } from 'src/app/_helpers/utils';
import { AltOptionTabToggleComponent } from 'src/app/_components/selection-controls/alt-option-tab-toggle/alt-option-tab-toggle.component';
import { UserService } from 'src/app/_services/user.service';
import {
  User,
  UserIndex,
  UserStatus,
  UserStatusPrimaryDisplay,
  UserStatusSecondaryLabel,
} from 'src/app/_models/user';
import { FormatRolePipe } from 'src/app/_helpers/format-role.pipe';
import { DropdownFormFieldComponent } from 'src/app/_components/forms/dropdown-form-field/dropdown-form-field.component';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { ToastComponent } from 'src/app/_components/toast/toast.component';
import { UnderlinedFormFieldComponent } from 'src/app/_components/forms/underlined-form-field/underlined-form-field.component';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import { StatusIndicatorComponent } from 'src/app/_components/chips/status-indicator/status-indicator.component';
import { EditLicenceTypeModalComponent } from 'src/app/_components/modals/edit-licence-type-modal/edit-licence-type-modal.component';
import { LicenceCardComponent } from 'src/app/_components/cards/licence-card/licence-card.component';
import { ButtonCardComponent } from 'src/app/_components/cards/button-card/button-card.component';

@Component({
  selector: 'app-organisation',
  imports: [
    IconButtonComponent,
    ButtonComponent,
    LoadingSpinnerComponent,
    ModalComponent,
    ValidationModalComponent,
    RoleCheckPipe,
    AltOptionTabToggleComponent,
    FormatRolePipe,
    DropdownFormFieldComponent,
    MatIcon,
    ErrorPageComponent,
    ToastComponent,
    UnderlinedFormFieldComponent,
    MatIconButtonComponent,
    StatusIndicatorComponent,
    EditLicenceTypeModalComponent,
    LicenceCardComponent,
    ButtonCardComponent,
    ReactiveFormsModule,
    MatTableModule,
  ],
  templateUrl: './organisation.component.html',
  styleUrl: './organisation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganisationComponent implements OnInit, OnDestroy {
  private readonly organisationService = inject(OrganisationService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthenticationService);
  private readonly modalService = inject(ModalService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private successToast?: ReturnType<typeof setTimeout>;
  protected pendingRequire2FA = false;
  private readonly unsubscribe$ = new Subject<void>();

  readonly currentUser = this.authService.currentUserValue;
  readonly Role = Role;
  readonly ogiSuperAdminOnly = OGI_SUPER_ADMIN_ONLY;
  readonly superAdminRoles = SUPER_ADMIN_ROLE_LIST;
  readonly UserStatus = UserStatus;
  readonly UserStatusPrimaryDisplay = UserStatusPrimaryDisplay;
  readonly UserStatusSecondaryLabel = UserStatusSecondaryLabel;
  readonly LicenseType = LicenseType;
  readonly LicenseDisplay = LicenseDisplay;
  readonly licenseLimits = LICENSE_LIMITS;

  readonly navOptions: TabOptions[] = [
    { value: 'members', labelText: 'Members' },
    { value: 'labs', labelText: 'Labs' },
  ];

  readonly membersDisplayedColumns = [
    'firstName',
    'lastName',
    'email',
    'status',
    'role',
    'laboratories',
    'actions',
  ];
  readonly labsDisplayedColumns = computed(() => [
    'name',
    'errorLogCount',
    'unviewedErrorLogCount',
    ...(this.currentUser.role === Role.OGI_Super_Administrator
      ? ['status']
      : []),
    'actions',
  ]);

  // State signals
  organisation = signal<OrganisationIndexView | null>(null);
  organisationId = signal<number | null>(null);
  selectedTabOption = signal('members');
  users = signal<User[]>([]);
  selectedUserIdForDeletion = signal<number | null>(null);

  // Loading / processing signals
  isLoading = signal(true);
  isProcessing = signal(false);
  membersTableLoading = signal(false);
  deleteUserProcessing = signal(false);
  deleteInviteProcessing = signal(false);
  resendInviteProcessing = signal(false);
  resendConfirmationEmailProcessing = signal(false);
  resendUpdateEmailConfirmationProcessing = signal(false);
  editLicenceTypeProcessing = signal(false);
  require2FAToggle = signal(false);
  require2FAProcessing = signal(false);

  // Error signals
  error = signal<ErrorObject | null>(null);
  getOrgError = signal<ErrorObject | null>(null);
  getOrgUsersError = signal<ErrorObject | null>(null);

  // Toast signals
  successResponse = signal<string | null>(null);
  displayToast = signal(false);

  // Sort signal
  currentSort = signal({ sortBy: 'FirstName', sortDirection: 'asc' });

  // Pagination signals
  pageRequested = signal(1);
  pageSize = signal(5);
  totalCount = signal(0);
  totalPages = signal(0);
  currentPage = signal(1);
  hasNext = signal(false);
  hasPrevious = signal(false);

  // Derived state
  readonly hasUsers = computed(() => this.users().length > 0);

  usersPerPageField = new FormBase({
    key: 'usersPerPage',
    label: 'Page Size',
    type: 'dropdown',
    placeholder: '',
    disabled: false,
    required: false,
    value: '5',
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });

  orgNameField = new FormBase({
    key: 'name',
    label: 'Organisation Name',
    type: 'text',
    placeholder: 'Organisation Name',
    disabled: false,
    required: true,
    value: '',
  });

  editOrgForm = this.formBuilder.group({
    [this.orgNameField.key]: [
      {
        value: this.orgNameField.value,
        disabled:
          this.currentUser.role !== Role.OGI_Super_Administrator &&
          this.currentUser.role !== Role.Super_User,
      },
      [Validators.required, Validators.minLength(3), Validators.maxLength(250)],
    ],
  });

  usersTableControls = this.formBuilder.group({
    [this.usersPerPageField.key]: new FormControl(this.usersPerPageField.value),
  });

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((params: Params) => {
        const id = Number(params['id']);
        this.organisationId.set(id);
        // Keep the org-warning-banner in sync when the org id changes via
        // direct URL navigation, not just via the overview list/modal flows.
        this.authService.setCurrentOrg(id);
      });

    this.usersTableControls.controls[this.usersPerPageField.key].valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((numOfUsers) => {
        this.pageSize.set(Number(numOfUsers));
        this.pageRequested.set(1);
        this.getOrganisationUsers();
      });

    this.getOrganisation();
  }

  onEditUser(userId: number): void {
    if (this.currentUser.id === userId) {
      void this.router.navigate(['account/user-profile']);
    } else {
      void this.router.navigate([`user/update-user/${userId}`]);
    }
  }

  getOrganisation(): void {
    this.organisationService
      .getOrganisation(this.organisationId())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (organisation: OrganisationIndexView) => {
          this.organisation.set(organisation);
          this.require2FAToggle.set(organisation.requiresTwoFactor ?? false);
          this.updateFormValues(organisation);
          this.isLoading.set(false);
          this.getOrganisationUsers();
        },
        error: (err: ErrorObject) => {
          this.getOrgError.set(err);
          this.isLoading.set(false);
        },
      });
  }

  updateFormValues(org: OrganisationIndexView): void {
    this.editOrgForm.reset();
    this.editOrgForm.patchValue({
      [this.orgNameField.key]: org.organisationName,
    });
    this.editOrgForm.markAsPristine();
  }

  getOrganisationUsers(): void {
    if (this.membersTableLoading()) {
      return;
    }

    this.membersTableLoading.set(true);
    this.getOrgUsersError.set(null);

    const { sortBy, sortDirection } = this.currentSort();

    this.userService
      .getOrganisationUsers(
        this.organisationId(),
        sortBy,
        sortDirection,
        this.pageRequested(),
        this.pageSize(),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: UserIndex) => {
          const { users, paginationData } = response;
          const {
            CurrentPage,
            PageSize,
            TotalPages,
            TotalCount,
            HasNext,
            HasPrevious,
          } = paginationData;

          this.users.set(users);
          this.currentPage.set(CurrentPage);
          this.pageSize.set(PageSize);
          this.totalCount.set(TotalCount);
          this.totalPages.set(TotalPages);
          this.hasNext.set(HasNext);
          this.hasPrevious.set(HasPrevious);
          this.membersTableLoading.set(false);
        },
        error: (err: ErrorObject) => {
          this.getOrgUsersError.set(err);
          this.membersTableLoading.set(false);
        },
      });
  }

  onSubmit(): void {
    if (this.editOrgForm.invalid || this.isProcessing()) return;

    this.isProcessing.set(true);
    this.error.set(null);
    this.successResponse.set(null);

    this.organisationService
      .updateOrganisation(this.editOrgForm.value.name, this.organisation().id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: string) => {
          this.isProcessing.set(false);
          this.organisation.update((org) => ({
            ...org,
            organisationName: this.editOrgForm.value.name,
          }));
          this.editOrgForm.markAsPristine();
          this.successResponse.set(response);
          this.showToast();
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.isProcessing.set(false);
          this.modalService.open('error');
        },
      });
  }

  onSort(sortTerm: string): void {
    this.currentSort.update((sort) => {
      if (sort.sortBy === sortTerm) {
        return {
          ...sort,
          sortDirection: sort.sortDirection === 'asc' ? 'desc' : 'asc',
        };
      }
      return { sortBy: sortTerm, sortDirection: 'asc' };
    });
    this.getOrganisationUsers();
  }

  showToast(): void {
    this.displayToast.set(true);
    this.successToast = setTimeout(() => {
      this.displayToast.set(false);
    }, 5000);
  }

  getPrevious(): void {
    this.pageRequested.set(this.currentPage() - 1);
    this.getOrganisationUsers();
  }

  getNext(): void {
    this.pageRequested.set(this.currentPage() + 1);
    this.getOrganisationUsers();
  }

  resendInvite(userId: number): void {
    if (this.resendInviteProcessing()) return;

    this.resendInviteProcessing.set(true);
    this.error.set(null);
    this.successResponse.set(null);

    this.userService
      .resendUserInvite(userId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.resendInviteProcessing.set(false);
          this.successResponse.set('User invite resent');
          this.showToast();
          this.getOrganisationUsers();
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.resendInviteProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  handleInviteDeletion(userId: number): void {
    this.selectedUserIdForDeletion.set(userId);
    this.modalService.open('delete-invite');
  }

  deleteInvite(): void {
    if (this.deleteInviteProcessing()) return;

    this.deleteInviteProcessing.set(true);
    this.error.set(null);
    this.successResponse.set(null);

    this.userService
      .deleteUserInvite(this.selectedUserIdForDeletion())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.deleteInviteProcessing.set(false);
          this.successResponse.set('User invite deleted');
          this.selectedUserIdForDeletion.set(null);
          this.closeModal('delete-invite');
          this.showToast();
          this.getOrganisationUsers();
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.deleteInviteProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  deleteOrganisation(): void {
    if (this.isProcessing()) return;

    this.isProcessing.set(true);
    this.error.set(null);
    this.successResponse.set(null);

    this.organisationService
      .deleteOrganisation(this.organisation().id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: string) => {
          this.isProcessing.set(false);
          this.closeModal('delete-organisation');
          this.successResponse.set(response);
          this.showToast();
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.isProcessing.set(false);
          this.modalService.open('error');
        },
      });
  }

  resendConfirmationEmail(userId: number): void {
    if (this.resendConfirmationEmailProcessing()) return;

    this.resendConfirmationEmailProcessing.set(true);
    this.error.set(null);
    this.successResponse.set(null);

    this.userService
      .resendConfirmation(userId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.resendConfirmationEmailProcessing.set(false);
          this.successResponse.set('Confirmation email resent');
          this.showToast();
          this.getOrganisationUsers();
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.resendConfirmationEmailProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  resendUpdateEmailConfirmation(userId: number): void {
    if (this.resendUpdateEmailConfirmationProcessing()) return;

    this.resendUpdateEmailConfirmationProcessing.set(true);
    this.error.set(null);
    this.successResponse.set(null);

    this.userService
      .resendUpdateEmailConfirmation(userId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.resendUpdateEmailConfirmationProcessing.set(false);
          this.successResponse.set('Update email confirmation resent');
          this.showToast();
          this.getOrganisationUsers();
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.resendUpdateEmailConfirmationProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  handleUserDeletion(userId: number): void {
    this.selectedUserIdForDeletion.set(userId);
    this.modalService.open('delete-user');
  }

  deleteUser(): void {
    if (this.deleteUserProcessing()) return;

    this.deleteUserProcessing.set(true);
    this.error.set(null);
    this.successResponse.set(null);

    this.userService
      .deleteUser(this.selectedUserIdForDeletion())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.deleteUserProcessing.set(false);
          this.successResponse.set('User deleted successfully');
          this.selectedUserIdForDeletion.set(null);
          this.closeModal('delete-user');
          this.getOrganisationUsers();
          this.showToast();
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.deleteUserProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  onViewLab(laboratory: Laboratory): void {
    const enrichedLab =
      resolveLabOwnership(laboratory, this.currentUser.laboratories) ??
      laboratory;
    this.authService.setSelectedLaboratory(enrichedLab);
    if (this.currentUser.role === Role.Super_User && !enrichedLab.isOwnLab) {
      void this.router.navigate(['/laboratories/view-lab']);
    } else {
      void this.router.navigate(['/dashboard']);
    }
  }

  onRowClick(user: User): void {
    if (
      user.userStatus === UserStatus.Active ||
      user.userStatus === UserStatus.PendingUpdateEmailConfirmation ||
      user.userStatus === UserStatus.PendingUpdateEmailConfirmationExpired
    ) {
      this.onEditUser(user.id);
    }
  }

  addLabButtonClicked(): void {
    void this.router.navigate(['/laboratories/add-lab'], {
      state: { data: this.organisation().id },
    });
  }

  onLicenceTypeConfirmed(licenseType: LicenseType): void {
    if (this.editLicenceTypeProcessing()) return;

    this.editLicenceTypeProcessing.set(true);
    this.error.set(null);
    this.successResponse.set(null);

    this.organisationService
      .updateLicenseType(this.organisation().id, licenseType)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ message, approvalPending }) => {
          this.editLicenceTypeProcessing.set(false);
          this.closeModal('edit-licence-type');
          if (!approvalPending) {
            this.organisation.update((org) => ({ ...org, licenseType }));
          }
          this.successResponse.set(message);
          this.showToast();
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.editLicenceTypeProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  onRequire2FAChanged(enabled: boolean): void {
    this.pendingRequire2FA = enabled;
    this.openModal(enabled ? 'require-2fa-validation' : 'disable-2fa');
  }

  onRequire2FAModalButtonClicked(buttonId: string): void {
    if (buttonId === 'confirm-require-2fa') {
      this.updateRequire2FA(this.pendingRequire2FA);
    } else {
      this.require2FAToggle.set(!this.pendingRequire2FA);
      this.closeModal(this.pendingRequire2FA ? 'require-2fa-validation' : 'disable-2fa');
      this.pendingRequire2FA = false;
    }
  }

  updateRequire2FA(require2FA: boolean): void {
    if (this.require2FAProcessing()) return;

    const modalId = require2FA ? 'require-2fa-validation' : 'disable-2fa';

    this.require2FAProcessing.set(true);
    this.error.set(null);
    this.successResponse.set(null);

    this.organisationService
      .updateRequire2FA(this.organisation().id, require2FA)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.require2FAProcessing.set(false);
          this.closeModal(modalId);
          this.require2FAToggle.set(require2FA);
          this.organisation.update((org) => ({
            ...org,
            requiresTwoFactor: require2FA,
          }));
          this.successResponse.set(
            `Two-factor authentication requirement ${
              require2FA ? 'enabled' : 'disabled'
            } successfully`,
          );
          this.showToast();
        },
        error: (err: ErrorObject) => {
          this.error.set(err);
          this.require2FAProcessing.set(false);
          this.require2FAToggle.set(!require2FA);
          this.closeModal(modalId);
          this.openModal('error');
        },
      });
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  inviteModalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'close-delete-invite':
        this.selectedUserIdForDeletion.set(null);
        this.modalService.close('delete-invite');
        break;
      case 'delete-invite':
        this.deleteInvite();
        break;
      case 'close-delete-user':
        this.selectedUserIdForDeletion.set(null);
        this.modalService.close('delete-user');
        break;
      case 'delete-user':
        this.deleteUser();
        break;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    if (this.successToast) {
      clearTimeout(this.successToast);
    }
  }
}
